<template>
  <Grid
    class="schedule-b-grid"
    :pageable="pageable"
    :data-items="products"
    :skip="skip"
    :take="take"
    :page-size="take"
    :total="total"
    :columns="columns"
    :loader="loading"
    :filter="filter"
    :column-menu="!loading"
    :resizable="true"
    selected-field="selected"
    :sort="sort"
    :reorderable="true"
    @columnresize="onColumnResize"
    @columnreorder="onColumnReorder"
    @filterchange="filterChangeHandler"
    @pagechange="pageChangeHandler"
    @selectionchange="changeSelection"
    @sortchange="sortChangeHandler"
    @rowclick="changeSelection"
    @headerselectionchange="changeHeaderSelection"
    data-cy="schedule-b-grid"
  >
    <template v-slot:columnMenu="{ props }">
      <ColumnMenu
        :column="props.column"
        :filterable="props.filterable"
        :filter="props.filter"
        :sortable="props.sortable"
        :sort="props.sort"
        :columns="columns"
        :getItems="getPropValues"
        @sortchange="(e: any) => props.onSortchange(e)"
        @filterchange="(e: any) => props.onFilterchange(e)"
        @closemenu="(e) => props.onClosemenu(e)"
        @contentfocus="(e) => props.onContentfocus(e)"
        @columnssubmit="onColumnsSubmit"
      />
    </template>
    <template v-slot:SelectCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        <input
          type="checkbox"
          v-model="dataItem[field]"
          @change="emit('selectionChanged', [dataItem])"
        />
      </td>
    </template>
    <template v-slot:DetailsCell="{ props: { dataItem } }">
      <td class="k-table-td">
        <input
          class="fa fa-external-link details-icon"
          type="image"
          alt="Detail Icon"
          @click.prevent="openDetails(dataItem)"
          :src="detailIconUrl"
          data-cy="details-action"
        />
      </td>
    </template>
    <template v-slot:LabelTypeCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        {{ getLabelTypeString(dataItem[field]) }}
      </td>
    </template>
    <template v-slot:DpmCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        {{ EnumDpm[dataItem[field]] }}
      </td>
    </template>
    <template v-slot:GroupCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        {{ convertNumber(dataItem[field]) }}
      </td>
    </template>
    <template v-slot:UkdCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        {{ dataItem[field] }}
      </td>
    </template>
    <template v-slot:PassoverCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        {{ dataItem[field] ? "Y" : "N" }}
      </td>
    </template>
    <template v-slot:SpecialRequirementsCell="{ props: { dataItem } }">
      <td class="k-table-td">
        {{ dataItem.SpecialRequirementsExists ? "Y" : "N" }}
      </td>
    </template>
    <template v-slot:RepackCell="{ props: { dataItem } }">
      <td class="k-table-td">
        {{ dataItem.IsRepack }}
      </td>
    </template>
    <template v-slot:StatusCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        {{ enumHelper.splitByCapitalLetter(EnumMerchStatus[dataItem[field]]) }}
      </td>
    </template>
  </Grid>
</template>

<script setup lang="ts">
import {
  Grid,
  type GridColumnProps,
  type GridColumnReorderEvent,
  type GridColumnResizeEvent,
  type GridFilterChangeEvent,
  type GridHeaderSelectionChangeEvent,
  type GridPageChangeEvent,
  type GridPagerSettings,
  type GridRowClickEvent,
  type GridSelectionChangeEvent,
} from "@progress/kendo-vue-grid";
import { ref, watchEffect } from "vue";
import type {
  CompositeFilterDescriptor,
  SortDescriptor,
} from "@progress/kendo-data-query";
import ColumnMenu from "@/shared/ui/inputs/ColumnMenu.vue";

import detailIconUrl from "@/assets/img/DetailIcon.png";
import { EnumDpm } from "@/Domain/EnumDpm";
import type { ProductWithSelection } from "@/api/kosherCertificates";
import { convertNumber } from "@/Domain/EnumGroupConverter";
import { EnumMerchStatus } from "@/Domain/EnumMerchStatus";
import type { ItemSortChangedOptions } from "@/Domain/ItemChangedOptions";
import { getLabelTypeString } from "@/shared/helpers/enumHelper";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";
import { GridOptionsService } from "@/shared/services/GridOptionsService";
import { GridMenuActionType } from "@/Domain/GridMenuActionType";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import {
  getFilterOperatorsForEnum,
  getFilterOperatorsForNumber,
} from "@/shared/helpers/filtersHelper";
import enumHelper from "@/shared/helpers/enumHelper";

const notificationStore = useNotificationStore();

const props = defineProps<{
  products: ProductWithSelection[];
  loading: boolean;
  skip: number;
  pageSize: number;
  total: number;
  filter: CompositeFilterDescriptor;
  sort: SortDescriptor[];
  getPropValues: (
    column: GridColumnPropsExtended
  ) => Promise<ProductWithSelection[]>;
}>();

const emit = defineEmits<{
  (e: "detailsClicked", products: ProductWithSelection): void;
  (
    e: "productsChanged",
    skip: number,
    take: number,
    filterEvent: GridFilterChangeEvent | null
  ): void;
  (e: "selectionChanged", selectedInvoices: ProductWithSelection[]): void;
  (e: "sortChanged", options: ItemSortChangedOptions): void;
  (e: "columnreorder", event: GridColumnReorderEvent): void;
  (e: "columnssubmit", columnsProps: GridColumnProps[]): void;
}>();

const gridName = "scheduleBGrid";

const skip = ref<number>(props.skip);
const take = ref<number>(props.pageSize);

const columns = ref<GridColumnPropsExtended[]>([
  {
    field: "selected",
    width: "50px",
    sortable: false,
    filterable: false,
    headerClassName: "hide-menu",
    hideInColumnMenu: true,
  },
  {
    title: "Details",
    cell: "DetailsCell",
    headerClassName: "hide-menu",
    sortable: false,
    filterable: false,
    width: "40px",
    hideInColumnMenu: true,
  },
  {
    title: "Label Number",
    field: "LabelNumber",
    columnMenu: "columnMenu",
  },
  {
    title: "Label Name",
    field: "LabelName",
    columnMenu: "columnMenu",
    hideInColumnMenu: true,
  },
  {
    title: "Brand Name",
    field: "BrandName",
    columnMenu: "columnMenu",
  },
  {
    title: "Label Company",
    field: "DistributorName",
    columnMenu: "columnMenu",
  },
  {
    title: "Label Type",
    field: "_LabelType",
    cell: "LabelTypeCell",
    columnMenu: "columnMenu",
    allowedFilterOperators: getFilterOperatorsForEnum(),
  },
  {
    title: "Symbol",
    field: "Symbol",
    columnMenu: "columnMenu",
  },
  {
    title: "DPM",
    field: "_Dpm",
    cell: "DpmCell",
    columnMenu: "columnMenu",
    allowedFilterOperators: getFilterOperatorsForEnum(),
  },
  {
    title: "GRP",
    field: "_Group",
    cell: "GroupCell",
    columnMenu: "columnMenu",
    allowedFilterOperators: getFilterOperatorsForNumber(),
  },
  {
    title: "UKD ID",
    field: "VueFullUkdIdWithSuppressUkdId",
    cell: "UkdCell",
    columnMenu: "columnMenu",
  },
  {
    title: "Passover",
    field: "_Pesach",
    cell: "PassoverCell",
    columnMenu: "columnMenu",
    allowedFilterOperators: getFilterOperatorsForEnum(),
  },
  {
    title: "Comment / Stipulation",
    field: "SpecialRequirementsExists",
    cell: "SpecialRequirementsCell",
    columnMenu: "columnMenu",
    allowedFilterOperators: getFilterOperatorsForEnum(),
  },
  {
    title: "Repack",
    field: "Dist",
    cell: "RepackCell",
    columnMenu: "columnMenu",
    allowedFilterOperators: getFilterOperatorsForEnum(),
  },
  {
    title: "Plant Name",
    field: "PlantName",
    columnMenu: "columnMenu",
  },
  {
    title: "Product Status",
    field: "_Status",
    cell: "StatusCell",
    columnMenu: "columnMenu",
    allowedFilterOperators: getFilterOperatorsForEnum(),
  },
]);

let pageSizes = [10, 20, 25, 50, 100];
if (!pageSizes.includes(take.value)) pageSizes.push(take.value);
pageSizes = pageSizes.sort((a, b) => a - b);
const pageable = ref<GridPagerSettings>({
  buttonCount: 5,
  info: true,
  type: "numeric",
  pageSizes: pageSizes,
  previousNext: true,
});

GridOptionsService.loadGridOptions(gridName, columns.value);

const filterChangeHandler = function (e: GridFilterChangeEvent) {
  skip.value = 0;
  emit("productsChanged", 0, take.value, e);
};

const sortChangeHandler = function (e: any) {
  const descriptors = e.sort as SortDescriptor[];
  if (!descriptors) return;
  skip.value = 0;
  emit("sortChanged", {
    field: descriptors[0].field,
    ascending: descriptors[0].dir === "asc",
  });
};

const pageChangeHandler = function (event: GridPageChangeEvent) {
  skip.value = event.page.skip;
  take.value = event.page.take;
  emit("productsChanged", event.page.skip, event.page.take, null);
};

const changeSelection = function (
  event: GridSelectionChangeEvent | GridRowClickEvent
) {
  // if clicked on the details icon or download link,
  // don't change the selection
  const className = event.event?.target?.className;
  if (
    className.includes &&
    (className.includes("details-icon") ||
      className.includes("is-download-link"))
  )
    return;

  const product = event.dataItem as ProductWithSelection;
  if (!product) return;
  product.selected = !product.selected;
  emit("selectionChanged", [product]);
};

const changeHeaderSelection = function (event: GridHeaderSelectionChangeEvent) {
  const checked = event.event.target.checked;
  props.products.forEach((product) => {
    product.selected = checked;
  });
  emit("selectionChanged", props.products);
};

const onColumnsSubmit = function (columnsProps: GridColumnProps[]) {
  if (columnsProps) {
    columns.value = columnsProps;
    GridOptionsService.saveVisibleColumns(gridName, columnsProps);
  }
  console.debug("ScheduleBGrid.vue, onColumnSubmit", columnsProps);

  emit("columnssubmit", columnsProps);
};

const onColumnResize = function (e: GridColumnResizeEvent) {
  const col = GridOptionsService.getResizedColumn(e, columns.value);
  if (col.title)
    GridOptionsService.saveColumnWidth(gridName, col.title, e.newWidth);
};

const onColumnReorder = function (e: GridColumnReorderEvent) {
  GridOptionsService.setSelectDetailsColumnOrder(e);
  columns.value = e.columns;
  GridOptionsService.saveColumnsOrder(gridName, e.columns);
  emit("columnreorder", e);
};

const openDetails = function (product: ProductWithSelection) {
  emit("detailsClicked", product);
};

const resetColumnSelection = function () {
  columns.value.forEach((col) => {
    col.hidden = col.defaultHiddenValue ?? false;
  });
  GridOptionsService.clearVisibleColumns(gridName);
};

const resetColumnSizes = function (showNotification = true) {
  GridOptionsService.clearColumnWidths(gridName);
  if (showNotification) {
    notificationStore.notify({
      type: NotificationTypeEnum.success,
      message:
        "Column sizes reset successfully. Please refresh the page to see the changes.",
      duration: 5000,
    });
  }
};

const resetColumnOrder = function () {
  columns.value.forEach((col) => {
    col.orderIndex = undefined;
  });
  GridOptionsService.clearColumnsOrder(gridName);
};

const resetAllSettings = function () {
  resetColumnSelection();
  resetColumnSizes(false);
  resetColumnOrder();

  notificationStore.notify({
    type: NotificationTypeEnum.success,
    message:
      "All settings reset successfully. Please refresh the page to see the changes.",
    duration: 5000,
  });
};

const executeAction = function (actionType: GridMenuActionType) {
  console.debug("ScheduleBGrid, executeAction", actionType);
  switch (actionType) {
    case GridMenuActionType.ResetColumnSelection:
      resetColumnSelection();
      break;
    case GridMenuActionType.ResetColumnSizes:
      resetColumnSizes();
      break;
    case GridMenuActionType.ResetColumnOrder:
      resetColumnOrder();
      break;
    case GridMenuActionType.ResetAllSettings:
      resetAllSettings();
      break;
  }
};

defineExpose({
  executeAction,
});

watchEffect(() => {
  skip.value = props.skip;
});

watchEffect(() => {
  take.value = props.pageSize;
});
</script>

<style scoped lang="scss">
.k-grid {
  .k-pager {
    .k-input-inner {
      padding: 0.375rem 0.375rem;
    }
  }
}

.schedule-b-grid {
  min-height: 400px;
  height: 800px;
}
</style>
